import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Plus, Check, Sparkles, Home, Link2 } from 'lucide-react'
import { toast } from 'react-toastify'
import { cn } from '@/features/utils'
import {
  selectHomeUserEmail,
  selectHomeUserDisplayName,
  selectIsWorkspaceContext,
  selectOrganizations,
  switchOrganization,
  switchToHomeWorkspace,
} from '../../../features/organizations/organizationsSlice'
import { selectProfile } from '../../../applications/Settings/settingsSlice'
import { selectRootUser } from '../../../rootSlice'
import {
  isOrgIdLabel,
  isSyntheticWorkspaceEmail,
  resolveAccountDisplayLabel,
  resolvePersonalAccountEmail,
} from '../../../features/organizations/accountDisplay'
import { pickDisplayProfileImageUrl } from '../../../modules/profileImageUrl'
interface OrgSwitcherMenuProps {
  onDismiss?: () => void
  onOpenCreateWizard?: () => void
  isAccountOwner?: boolean
}

function orgInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase()
  }
  const trimmed = name.trim()
  return (trimmed.slice(0, 2) || 'O').toUpperCase()
}

function formatPlanLabel(product: string | undefined | null): string {
  if (!product || product === 'Free') return 'Free plan'
  return `${product} plan`
}

const PLAN_PILL_STYLES: Record<string, string> = {
  Free: 'tw-bg-slate-100 tw-text-slate-600',
  Entrepreneur: 'tw-bg-violet-100 tw-text-violet-800',
  Professional: 'tw-bg-teal-100 tw-text-teal-800',
  Business: 'tw-bg-blue-100 tw-text-blue-800',
  'Legacy Business': 'tw-bg-sky-100 tw-text-sky-800',
  Enterprise: 'tw-bg-amber-100 tw-text-amber-900',
  Startup: 'tw-bg-indigo-100 tw-text-indigo-800',
}

function planPillClass(product: string | undefined | null): string {
  return PLAN_PILL_STYLES[product || 'Free'] || 'tw-bg-slate-100 tw-text-slate-700'
}

function orgRoleLabel(role: string | undefined | null): string {
  if (role === 'owner') return 'Owner'
  if (role === 'admin') return 'Admin'
  return 'Member'
}

const ORG_ROLE_PILL_STYLES: Record<string, string> = {
  owner: 'tw-bg-amber-100 tw-text-amber-900',
  admin: 'tw-bg-violet-100 tw-text-violet-800',
  member: 'tw-bg-slate-100 tw-text-slate-600',
}

function orgRolePillClass(role: string | undefined | null): string {
  const key = role === 'owner' || role === 'admin' ? role : 'member'
  return ORG_ROLE_PILL_STYLES[key]
}

function OrgRolePill({ role }: { role: string | undefined | null }) {
  if (!role) return null
  return (
    <span
      className={cn(
        'tw-inline-flex tw-shrink-0 tw-rounded-full tw-px-1.5 tw-py-0.5 tw-text-[10px] tw-font-semibold tw-uppercase tw-tracking-wide tw-leading-none',
        orgRolePillClass(role)
      )}
    >
      {orgRoleLabel(role)}
    </span>
  )
}

function UserMenuAvatar({
  imageUrl,
  initial,
  sizeClass = 'tw-h-8 tw-w-8 tw-text-xs',
}: {
  imageUrl: string | null
  initial: string
  sizeClass?: string
}) {
  const [imgError, setImgError] = React.useState(false)
  const showImage = imageUrl && !imgError

  React.useEffect(() => {
    setImgError(false)
  }, [imageUrl])

  return (
    <span
      className={cn(
        'tw-relative tw-flex tw-flex-shrink-0 tw-items-center tw-justify-center tw-overflow-hidden tw-rounded-full tw-bg-slate-100 tw-text-sm tw-font-semibold tw-uppercase tw-text-slate-700',
        sizeClass
      )}
    >
      {showImage ? (
        <img
          key={imageUrl}
          src={imageUrl}
          alt=""
          referrerPolicy="no-referrer"
          className="tw-absolute tw-inset-0 tw-h-full tw-w-full tw-object-cover"
          onError={() => setImgError(true)}
        />
      ) : (
        initial
      )}
    </span>
  )
}

function OrgAvatar({
  name,
  isActive,
  logoUrl,
}: {
  name: string
  isActive: boolean
  logoUrl?: string | null
}) {
  const [imgError, setImgError] = React.useState(false)
  const showImage = logoUrl && !imgError

  React.useEffect(() => {
    setImgError(false)
  }, [logoUrl])

  return (
    <span
      className={cn(
        'tw-relative tw-flex tw-h-8 tw-w-8 tw-flex-shrink-0 tw-items-center tw-justify-center tw-overflow-hidden tw-rounded-md tw-text-xs tw-font-semibold tw-uppercase',
        isActive
          ? 'tw-bg-blue-100 tw-text-blue-800'
          : 'tw-bg-slate-100 tw-text-slate-600'
      )}
    >
      {showImage ? (
        <img
          key={logoUrl}
          src={logoUrl}
          alt=""
          referrerPolicy="no-referrer"
          className="tw-absolute tw-inset-0 tw-h-full tw-w-full tw-object-cover"
          onError={() => setImgError(true)}
        />
      ) : (
        orgInitials(name)
      )}
    </span>
  )
}

/** Org switcher block embedded at the top of the sidebar account menu. */
export default function OrgSwitcherMenu({
  onDismiss,
  onOpenCreateWizard,
  isAccountOwner = true,
}: OrgSwitcherMenuProps) {
  const dispatch = useDispatch()
  const organizations = useSelector(selectOrganizations)
  const isWorkspaceContext = useSelector(selectIsWorkspaceContext)
  const homeUserEmail = useSelector(selectHomeUserEmail)
  const homeUserDisplayName = useSelector(selectHomeUserDisplayName)
  const user = useSelector(selectRootUser)
  const profile = useSelector(selectProfile)
  const currentPlanProduct = useSelector(
    (state: { plan?: { subscription?: { plans?: { product?: string }[] } } }) =>
      state.plan?.subscription?.plans?.[0]?.product
  )

  const activeOrg = organizations.find((org) => org.is_active)
  const workspaceEmail = user?.email || ''
  const personalEmail = resolvePersonalAccountEmail(user, homeUserEmail)
  const isOrgWorkspaceGuest =
    isWorkspaceContext &&
    !!activeOrg &&
    activeOrg.role !== 'owner' &&
    isSyntheticWorkspaceEmail(workspaceEmail)
  const displayName = isOrgWorkspaceGuest
    ? resolveAccountDisplayLabel({
        profileDisplayName: profile?.displayName,
        userName: user?.name,
        personalEmail,
      })
    : activeOrg?.display_name ||
      activeOrg?.name ||
      user?.organization_name ||
      [profile?.displayName, user?.name].find((v) => v && !isOrgIdLabel(v)) ||
      personalEmail ||
      workspaceEmail ||
      'Account'
  const displayEmail = isWorkspaceContext ? personalEmail : workspaceEmail
  // The synthetic org workspace address isn't meaningful to show.
  const showEmail = !!displayEmail && !isSyntheticWorkspaceEmail(displayEmail)
  const displayInitial = (displayName || 'U').trim().charAt(0).toUpperCase()
  const profileImageUrl = pickDisplayProfileImageUrl(
    profile?.profileImageUrl,
    activeOrg?.logo_url
  )
  const planProduct = activeOrg?.billing_summary?.product || currentPlanProduct || 'Free'
  const planLabel = formatPlanLabel(planProduct)
  const showPlanSparkle = planProduct !== 'Free'
  const showReturnHome =
    isWorkspaceContext &&
    !!personalEmail &&
    !!workspaceEmail &&
    personalEmail !== workspaceEmail &&
    isSyntheticWorkspaceEmail(workspaceEmail)
  const personalAccountLabel = homeUserDisplayName || personalEmail || 'Account'

  const sortedOrganizations = React.useMemo(() => {
    return [...organizations].sort((a, b) => {
      if (a.is_active === b.is_active) {
        const an = (a.display_name || a.name || '').toLowerCase()
        const bn = (b.display_name || b.name || '').toLowerCase()
        return an.localeCompare(bn)
      }
      return a.is_active ? -1 : 1
    })
  }, [organizations])

  const handleSwitch = async (organizationId: string) => {
    onDismiss?.()
    const result = await dispatch(switchOrganization(organizationId) as never)
    if (switchOrganization.fulfilled.match(result)) {
      window.location.reload()
      return
    }
    if (switchOrganization.rejected.match(result)) {
      window.alert((result.payload as string) || 'Could not switch organization')
    }
  }

  const handleSwitchHome = async () => {
    onDismiss?.()
    const result = await dispatch(switchToHomeWorkspace() as never)
    if (switchToHomeWorkspace.fulfilled.match(result)) {
      window.location.reload()
    }
  }

  const handleCopyOrgLink = async (organizationId: string) => {
    try {
      const url = new URL(window.location.href)
      url.searchParams.set('org', organizationId)
      await navigator.clipboard.writeText(url.toString())
      toast.success('Organization link copied')
    } catch {
      toast.error('Could not copy link')
    }
  }

  return (
    <div className="tw-border-b tw-border-slate-200 tw-pb-1">
      <div className="tw-flex tw-items-center tw-gap-2.5 tw-px-3 tw-py-2">
        <UserMenuAvatar imageUrl={profileImageUrl} initial={displayInitial} />
        <div className="tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-gap-1">
          <div className="tw-flex tw-min-w-0 tw-items-center tw-gap-2">
            <p className="tw-m-0 tw-min-w-0 tw-flex-1 tw-truncate tw-text-sm tw-font-semibold tw-leading-tight tw-text-slate-900">
              {displayName}
            </p>
            {activeOrg ? <OrgRolePill role={activeOrg.role} /> : null}
            <span
              className={cn(
                'tw-inline-flex tw-shrink-0 tw-items-center tw-gap-1 tw-rounded-full tw-px-2 tw-py-0.5 tw-text-xs tw-font-medium tw-leading-none',
                planPillClass(planProduct)
              )}
            >
              {showPlanSparkle && <Sparkles className="tw-h-3 tw-w-3 tw-shrink-0" aria-hidden />}
              {planLabel}
            </span>
          </div>
          {showEmail ? (
            <p className="tw-m-0 tw-truncate tw-text-sm tw-leading-tight tw-text-slate-500">{displayEmail}</p>
          ) : null}
        </div>
      </div>

      <div>
        {organizations.length > 0 && (
          <p className="tw-m-0 tw-px-3 tw-pb-0 tw-pt-1.5 tw-text-[10px] tw-font-medium tw-uppercase tw-tracking-wide tw-text-slate-400">
            Organizations
          </p>
        )}
        <div className="tw-max-h-40 tw-overflow-y-auto tw-pt-0">
          {sortedOrganizations.map((org) => {
            const orgName = org.display_name || org.name
            const orgLogoUrl = pickDisplayProfileImageUrl(
              org.is_active ? profile?.profileImageUrl : null,
              org.logo_url
            )
            return (
            <div
              key={org.id}
              className={cn(
                'tw-flex tw-w-full tw-items-center tw-gap-2 tw-px-2 tw-py-0.5',
                org.is_active && 'tw-bg-slate-50'
              )}
            >
              <button
                type="button"
                className="tw-flex tw-min-w-0 tw-flex-1 tw-items-center tw-gap-2 tw-rounded-md tw-py-1.5 tw-pl-1 tw-pr-0 tw-text-left hover:tw-bg-slate-100"
                onClick={() => handleSwitch(org.id)}
              >
                <OrgAvatar name={orgName} isActive={org.is_active} logoUrl={orgLogoUrl} />
                <span className="tw-min-w-0 tw-flex-1">
                  <span className="tw-flex tw-min-w-0 tw-items-center tw-gap-1.5">
                    <span className="tw-truncate tw-text-sm tw-text-slate-800">
                      {orgName}
                    </span>
                    <OrgRolePill role={org.role} />
                  </span>
                  {org.display_hint ? (
                    <span className="tw-block tw-truncate tw-text-xs tw-text-slate-500">
                      {org.display_hint}
                    </span>
                  ) : null}
                </span>
              </button>
              <button
                type="button"
                className="tw-flex tw-h-8 tw-w-8 tw-flex-shrink-0 tw-items-center tw-justify-center tw-rounded-md tw-text-slate-500 hover:tw-bg-slate-100 hover:tw-text-slate-700"
                aria-label={`Copy link to ${orgName}`}
                title="Copy organization link"
                onClick={() => void handleCopyOrgLink(org.id)}
              >
                <Link2 className="tw-h-4 tw-w-4" />
              </button>
              {org.is_active ? (
                <Check
                  className="tw-h-4 tw-w-4 tw-flex-shrink-0 tw-text-blue-600"
                  aria-label="Active organization"
                />
              ) : null}
            </div>
            )
          })}
        </div>
      </div>

      {showReturnHome ? (
        <button
          type="button"
          className="tw-flex tw-w-full tw-items-center tw-gap-2 tw-border-t tw-border-slate-200 tw-px-3 tw-py-2 tw-text-sm tw-text-slate-700 hover:tw-bg-slate-100"
          onClick={handleSwitchHome}
        >
          <Home className="tw-h-4 tw-w-4" />
          Return to {personalAccountLabel}
        </button>
      ) : null}
      {isAccountOwner ? (
        <button
          type="button"
          className="tw-flex tw-w-full tw-items-center tw-gap-2 tw-border-t tw-border-slate-200 tw-px-3 tw-py-2 tw-text-sm tw-text-slate-700 hover:tw-bg-slate-100"
          onClick={() => onOpenCreateWizard?.()}
        >
          <Plus className="tw-h-4 tw-w-4" />
          Create organization
        </button>
      ) : null}
    </div>
  )
}
